import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { apiErrorMessage } from 'i18n/apiError'
import { AppUseSelector } from '../../redux/hooks'
import Diff from 'containers/Diff'
import PLayer from 'containers/player/Player'
import Loader from 'containers/loader/loader'
import WordDetail from 'containers/word-detail'
import WordExamplesModal from 'containers/modal/word-examples-modal'
import WritingSpeakingSubmission, { WritingSpeakingPayload } from 'components/WritingSpeakingSubmission'
import {
  createRecordingUploadUrl,
  getTasterStatus,
  submitTaster,
  uploadRecordingToPresignedUrl,
} from '../../redux/actions/tasterAction'
import { getWordDetail } from '../../redux/actions/storyAction'
import { converterWord, getWordLine, wordSimplified, getSentenceWords } from 'utils/helperFunction'
import { getSignedUrl } from 'utils/S3Upload'
import { needsSigning } from 'utils/s3Key'
import { hapticSuccess } from 'utils/haptics'
import { uploadContentTypeFor } from 'shared/useRecorder'
import { CLAIM_OFFER, STORIES } from 'routes/routeConstant'
import { TasterStatus } from 'types/tasterTypes'
import './taster.css'

const SUBMIT_ERROR = 'offers:taster.submitError'

// The plan's name, shown as is in the upsell heading.
const PRO_NAME = 'maayot Pro'

// Recording references can be S3 keys (like the corrections page uses) or full URLs. A URL on
// our private bucket still has to be signed.
const resolveAudioUrl = async (value?: string | null): Promise<string> => {
  if (!value) return ''
  if (!needsSigning(value)) return value
  try {
    return await getSignedUrl({ key: value })
  } catch (e) {
    return ''
  }
}

/**
 * /taster — one free teacher correction for learners who have never used a Pro
 * correction, reached from the re-engagement email sequence. Renders one of
 * four states from GET taster/status: ready (writing + speaking form),
 * submitted (confirmation), corrected (teacher's correction + Pro upsell),
 * ineligible (friendly redirect to the stories page).
 */
const Taster: React.FC = () => {
  const { t } = useTranslation('offers')
  const navigate = useNavigate()
  const { chineseWordFontSize } = AppUseSelector(({ dashboard }: any) => dashboard)
  const { credentials } = AppUseSelector(({ user }: any) => user)
  const memberId = credentials?.memberId
  const characterPreference = credentials?.customFields?.character
  const [status, setStatus] = useState<TasterStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [yourAudioUrl, setYourAudioUrl] = useState('')
  const [teacherAudioUrl, setTeacherAudioUrl] = useState('')
  // One-tap dictionary popup state — same plumbing as the question/corrections pages
  const [activeIndex, setActiveIndex] = useState('-1')
  const [wordDetailVisible, setWordDetailVisible] = useState(false)
  const [openExampleModal, setOpenExampleModal] = useState(false)
  const [isImportanceOpen, setImportanceOpen] = useState(false)
  const [isMasteryOpen, setMasteryOpen] = useState(false)
  const contentWordRef = useRef<HTMLDivElement>(null)
  const extraDivRef = useRef<HTMLDivElement>(null)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const fetchStatus = async () => {
    const result = await getTasterStatus()
    if (!isMounted.current) return
    setStatus(result)
    setIsLoading(false)
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  // Resolve playable URLs for the corrected state (keys need signing, same as /correction)
  useEffect(() => {
    if (status?.state !== 'corrected') return
    resolveAudioUrl(status?.correction?.recordingUrl).then((url) => isMounted.current && setYourAudioUrl(url))
    resolveAudioUrl(status?.correction?.correctedRecordingUrl).then(
      (url) => isMounted.current && setTeacherAudioUrl(url),
    )
  }, [status])

  const handleSubmit = async ({ answer, recordingFile, speakingMode }: WritingSpeakingPayload) => {
    if (isSubmitting || !status?.story?.storyId) return
    setSubmitError('')
    setIsSubmitting(true)
    try {
      let recordingUrl: string | undefined
      if (recordingFile) {
        // Sign and upload for the format MediaRecorder really produced (WebM in Chrome, MP4 in Safari).
        const contentType = uploadContentTypeFor(recordingFile.type)
        const grant = await createRecordingUploadUrl(status.story.storyId, contentType)
        await uploadRecordingToPresignedUrl(grant.uploadUrl, recordingFile, contentType)
        recordingUrl = grant.key
      }
      await submitTaster({
        ...(answer ? { answer } : {}),
        ...(recordingUrl ? { recordingUrl, speakingMode } : {}),
      })
      hapticSuccess()
      if (!isMounted.current) return
      // Re-fetch rather than guessing: the backend is the authority on state.
      setIsLoading(true)
      await fetchStatus()
    } catch (error: any) {
      if (!isMounted.current) return
      setSubmitError(apiErrorMessage(error, SUBMIT_ERROR))
    } finally {
      if (isMounted.current) setIsSubmitting(false)
    }
  }

  const story = status?.story
  const correction = status?.correction

  // ----- One-tap dictionary (same handle pattern as the question/corrections pages) -----

  const handle = {
    onClickWord: (event: any, word: any, sentence: any, key: string) => {
      handle.translateWord(event, sentence)
      setActiveIndex(activeIndex === key ? '-1' : key)
      event.preventDefault()
      event.stopPropagation()
    },
    translateWord: (event: any, sentence: any) => {
      if (
        (event.type === 'mouseover' || event.type === 'click') &&
        event.target.innerText &&
        event.target.innerText.length > 0
      ) {
        const word = event.target.innerText
        if (/^[&.-”“，、；？@!#$%^&*()]+$/.test(word) || word.trim() === '。') {
          return
        }
        const allData = [...(story?.questionContent || []), ...(story?.firstSentenceContent || [])]
        const wordTranslate = allData.filter((iword) => converterWord(iword?.word, characterPreference) === word)
        if (wordTranslate && wordTranslate.length > 0) {
          handle.getWordDetail(word, sentence, wordTranslate[0])
        }
      }
    },
    getWordDetail: async (word: string, sentence: any, data: any) => {
      const sentenceString = getWordLine(sentence)
      const basicDetail = {
        word,
        pinyin: data?.pinyin,
        english: data?.english,
        sentenceString,
        sentenceWords: getSentenceWords(sentence),
      }
      if (word && memberId) {
        setWordDetailVisible(true)
        await getWordDetail({
          detail: basicDetail,
          payload: { word: wordSimplified(word), memberId },
          wordType: 'word',
        })
      }
    },
  }

  // Tapping anywhere else closes the popup (no mastery tracking here — the
  // taster has no read-story context to attribute word knowledge to).
  useEffect(() => {
    if (openExampleModal) return
    const handleClickOutside = (event: any) => {
      if (!contentWordRef.current?.contains(event.target) && !extraDivRef.current?.contains(event.target)) {
        setWordDetailVisible(false)
        setImportanceOpen(false)
        setMasteryOpen(false)
        setActiveIndex('-1')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openExampleModal, wordDetailVisible])

  const renderHeader = (kicker: string | null, title: string, lead?: string) => (
    <header className='taster-header'>
      {kicker && <span className='taster-kicker'>{kicker}</span>}
      <h1 className='taster-title'>{title}</h1>
      {lead && <p className='taster-lead'>{lead}</p>}
    </header>
  )

  const renderReady = () => (
    <>
      {renderHeader(
        null,
        t('taster.ready.title'),
        t('taster.ready.lead'),
      )}
      {story && (
        <WritingSpeakingSubmission
          question={story.question}
          firstSentence={story.firstSentence}
          questionContent={story.questionContent}
          firstSentenceContent={story.firstSentenceContent}
          handleClickWord={handle.onClickWord}
          activeIndex={activeIndex}
          submitLabel={t('taster.ready.submit')}
          isSubmitting={isSubmitting}
          errorMessage={submitError}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )

  const renderSubmitted = () => (
    <>
      {renderHeader(
        null,
        t('taster.submitted.title'),
        t('taster.submitted.lead'),
      )}
      <div className='taster-submitted-card'>
        <i className='fa fa-check-circle taster-submitted-icon' aria-hidden='true' />
        <div>
          <p className='taster-submitted-line'>{t('taster.submitted.line')}</p>
          <p className='taster-submitted-sub'>{t('taster.submitted.sub')}</p>
        </div>
      </div>
      <div className='taster-actions'>
        <button className='button-2 w-button green-btn-class' onClick={() => navigate(STORIES)}>
          {t('taster.submitted.cta')}
        </button>
      </div>
    </>
  )

  const renderCorrected = () => (
    <>
      {renderHeader(
        null,
        t('taster.corrected.title'),
        t('taster.corrected.lead'),
      )}

      {(correction?.recordingUrl || correction?.correctedRecordingUrl) && (
        <div className='w-row w-d-j-s-flex section-story-content taster-audio-row'>
          {correction?.recordingUrl && (
            <div className='w-col-6 w-col-medium-6 w-col-small-6 w-col-tiny-12 content-left box-padding'>
              <div className='correction-answer-box'>
                <span className='box-title mb-10'> {t('taster.corrected.yourRecording')}</span>
                {yourAudioUrl !== '' ? (
                  <div className='w-row w-col-11 w-col-medium-11 w-col-small-12 w-col-tiny-12 box-audio'>
                    <PLayer audio={yourAudioUrl} />
                  </div>
                ) : (
                  t('taster.corrected.audioNotFound')
                )}
              </div>
            </div>
          )}
          {correction?.correctedRecordingUrl && (
            <div className='w-col-6 w-col-medium-6 w-col-small-6 w-col-tiny-12 content-left'>
              <div className='correction-box'>
                <div className='w-row w-col-12 w-col-medium-11 w-col-small-12 w-col-tiny-12 box-audio'>
                  <span className='box-title'> {t('taster.corrected.teacherRecording')} </span>
                  {teacherAudioUrl !== '' ? <PLayer audio={teacherAudioUrl} /> : t('taster.corrected.audioNotFound')}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {correction?.answer && (
        <div className='taster-correction-block'>
          <div className='box-title mb-10'> {t('taster.corrected.yourWriting')} </div>
          <div className='story-content story-words taster-answer-text'>{correction.answer}</div>
        </div>
      )}

      {correction?.correctedAnswer && (
        <div className='taster-correction-block'>
          <div className='box-title mb-10'> {t('taster.corrected.teacherWriting')} </div>
          <div className='story-content story-words taster-answer-text'>{correction.correctedAnswer}</div>
        </div>
      )}

      {correction?.answer && correction?.correctedAnswer && (
        <div className='taster-correction-block'>
          <div className='box-title mb-10'> {t('taster.corrected.preview')} </div>
          <div className='story-content story-words'>
            <Diff
              string1={correction.answer}
              string2={correction.correctedAnswer}
              mode='characters'
              chineseWordFontSize={chineseWordFontSize}
              showPinYin={true}
            />
          </div>
        </div>
      )}

      {/* Upsell — this correction, every day, is the Pro plan */}
      <div className='taster-upsell'>
        <img src='/landing-assets/images/banner-bg.png' alt='' className='taster-upsell-bg' />
        <div className='taster-upsell-content'>
          <h2 className='taster-upsell-title'>
            <Trans
              t={t}
              i18nKey='taster.upsell.title'
              components={[
                <span key='0' className='taster-upsell-pro'>
                  <span className='taster-upsell-pro-text'>{PRO_NAME}</span>
                  <span className='taster-upsell-pro-glow' aria-hidden='true'>
                    {PRO_NAME}
                  </span>
                </span>,
              ]}
            />
          </h2>
          <p className='taster-upsell-lead'>{t('taster.upsell.lead')}</p>
          <div className='taster-upsell-sessions'>
            <div className='taster-upsell-video'>
              <video src='/videos/feedbacksession.mp4' autoPlay muted loop playsInline />
              <span className='taster-upsell-video-caption'>{t('taster.upsell.videoCaption')}</span>
            </div>
            <ul className='taster-upsell-session-list'>
              <li>
                <span className='taster-upsell-session-lead'>{t('taster.upsell.onboardingLead')}</span>
                <span className='taster-upsell-session-rest'>{t('taster.upsell.onboardingRest')}</span>
              </li>
              <li>
                <span className='taster-upsell-session-lead'>{t('taster.upsell.reviewsLead')}</span>
                <span className='taster-upsell-session-rest'>{t('taster.upsell.reviewsRest')}</span>
              </li>
            </ul>
          </div>
          <p className='taster-upsell-lead'>{t('common:teacherLanguageNotice')}</p>
          <button className='taster-upsell-btn' onClick={() => navigate(CLAIM_OFFER)}>
            <span className='taster-upsell-btn-label'>{t('taster.upsell.cta')}</span>
            <img src='/landing-assets/images/right-arrow2.svg' alt='' className='taster-upsell-btn-arrow' />
          </button>
        </div>
      </div>
    </>
  )

  const renderIneligible = () => (
    <>
      {renderHeader(
        t('taster.ineligible.kicker'),
        t('taster.ineligible.title'),
        t('taster.ineligible.lead'),
      )}
      <div className='taster-actions'>
        <Link to={STORIES} className='button-2 w-button green-btn-class taster-link-btn'>
          {t('taster.ineligible.cta')}
        </Link>
      </div>
    </>
  )

  return (
    <>
      <div className='taster-page'>
        <div className='taster-body w-container'>
          {isLoading ? (
            <Loader />
          ) : status?.state === 'ready' ? (
            renderReady()
          ) : status?.state === 'submitted' ? (
            renderSubmitted()
          ) : status?.state === 'corrected' ? (
            renderCorrected()
          ) : (
            renderIneligible()
          )}
        </div>
      </div>
      {/* One-tap dictionary popup, same components the question/corrections pages mount */}
      <WordDetail
        ref={contentWordRef}
        setOpenExampleModal={setOpenExampleModal}
        isImportanceOpen={isImportanceOpen}
        setImportanceOpen={setImportanceOpen}
        isMasteryOpen={isMasteryOpen}
        setMasteryOpen={setMasteryOpen}
        setWordDetailVisible={setWordDetailVisible}
        setActiveIndex={setActiveIndex}
        visible={wordDetailVisible}
        openExampleModal={openExampleModal}
        showMasterySection={false}
      />
      <WordExamplesModal
        ref={extraDivRef}
        openExampleModal={openExampleModal}
        setOpenExampleModal={setOpenExampleModal}
        setWordDetailVisible={setWordDetailVisible}
      />
    </>
  )
}

export default Taster
